/**
 * Battle audio. The music is a looping file from the rules (`ui.music`), routed
 * through the same master gain as the synthesised hit effects so the mute switch
 * covers everything. Without a file, a synthesised drone plays instead.
 */
class BattleAudio {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private ambient: { stop(): void } | null = null
  private music: { src: string; volume: number } | null = null
  /** Gain of the running music, so hits can duck it for a moment. */
  private musicGain: GainNode | null = null
  private musicVolume = 0.5

  /** Briefly lower the music so a hit is heard over it. */
  private duck(amount = 0.45, ms = 260) {
    if (!this.ctx || !this.musicGain) return
    const t = this.ctx.currentTime
    const g = this.musicGain.gain
    g.cancelScheduledValues(t)
    g.setValueAtTime(g.value, t)
    g.linearRampToValueAtTime(Math.max(0.0001, this.musicVolume * amount), t + 0.02)
    g.linearRampToValueAtTime(Math.max(0.0001, this.musicVolume), t + ms / 1000)
  }
  muted = false

  /** Configure the music track before or after the gate; a change while playing restarts it. */
  setMusic(src: string, volume = 0.5) {
    const changed = this.music?.src !== src || this.music?.volume !== volume
    this.music = src ? { src, volume } : null
    if (changed && this.ambient) {
      this.stopAmbient()
      this.startAmbient()
    }
  }

  constructor() {
    try {
      this.muted = localStorage.getItem('boss:muted') === '1'
    } catch {
      this.muted = false
    }
  }

  /** Must run inside a user gesture. */
  unlock() {
    if (!this.ctx) {
      this.ctx = new AudioContext()
      this.master = this.ctx.createGain()
      this.master.gain.value = this.muted ? 0 : 1
      // A limiter keeps stacked hits from clipping the output.
      const limiter = this.ctx.createDynamicsCompressor()
      limiter.threshold.value = -8
      limiter.knee.value = 4
      limiter.ratio.value = 16
      limiter.attack.value = 0.002
      limiter.release.value = 0.12
      this.master.connect(limiter).connect(this.ctx.destination)
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume()
  }

  setMuted(m: boolean) {
    this.muted = m
    try {
      localStorage.setItem('boss:muted', m ? '1' : '0')
    } catch {
      /* private mode etc. */
    }
    if (this.ctx && this.master) this.master.gain.setTargetAtTime(m ? 0 : 1, this.ctx.currentTime, 0.05)
  }

  /**
   * Start the music. Resolves true when it is audibly running, false when the browser
   * refused because there has been no user gesture yet (call again from a gesture).
   */
  async startAmbient(): Promise<boolean> {
    if (!this.ctx || !this.master) return false
    if (this.ambient) return this.ctx.state === 'running'
    if (this.music) return this.startTrack(this.music.src, this.music.volume)
    this.startDrone()
    if (this.ctx.state !== 'running') await this.ctx.resume().catch(() => undefined)
    return this.ctx.state === 'running'
  }

  /** Remembered visitors: try to start without a gesture; the caller falls back to the first tap. */
  async autostart(): Promise<boolean> {
    this.unlock()
    const ok = await this.startAmbient()
    if (!ok) this.stopAmbient()
    return ok
  }

  private async startTrack(src: string, volume: number): Promise<boolean> {
    const ctx = this.ctx!
    const el = new Audio(src)
    el.loop = true
    el.preload = 'auto'
    const source = ctx.createMediaElementSource(el)
    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0.0001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), ctx.currentTime + 2.5)
    source.connect(gain).connect(this.master!)
    this.musicGain = gain
    this.musicVolume = volume
    this.ambient = {
      stop: () => {
        this.musicGain = null
        gain.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.25)
        window.setTimeout(() => {
          el.pause()
          source.disconnect()
        }, 900)
      },
    }
    try {
      await el.play()
      if (ctx.state !== 'running') await ctx.resume()
      return ctx.state === 'running'
    } catch (err) {
      // NotAllowedError: no user gesture yet. Leave a clean slate for the retry.
      console.info('[audio] music waits for a tap:', err instanceof Error ? err.name : err)
      return false
    }
  }

  private startDrone() {
    const ctx = this.ctx!
    const master = this.master!
    const out = ctx.createGain()
    out.gain.setValueAtTime(0, ctx.currentTime)
    out.gain.linearRampToValueAtTime(0.35, ctx.currentTime + 3)
    out.connect(master)

    // Low drone: two detuned oscillators through a slowly breathing low-pass.
    const filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 220
    filter.connect(out)
    const lfo = ctx.createOscillator()
    lfo.frequency.value = 0.08
    const lfoGain = ctx.createGain()
    lfoGain.gain.value = 120
    lfo.connect(lfoGain).connect(filter.frequency)
    lfo.start()
    const oscs = [55, 82.4, 110.5].map((f, i) => {
      const o = ctx.createOscillator()
      o.type = i === 0 ? 'sawtooth' : 'triangle'
      o.frequency.value = f
      o.detune.value = (i - 1) * 6
      const g = ctx.createGain()
      g.gain.value = i === 0 ? 0.12 : 0.06
      o.connect(g).connect(filter)
      o.start()
      return o
    })

    // Wind: looped noise through a band-pass with a wandering gain.
    const noise = ctx.createBufferSource()
    const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
    noise.buffer = buf
    noise.loop = true
    const bp = ctx.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.value = 500
    bp.Q.value = 0.6
    const windGain = ctx.createGain()
    windGain.gain.value = 0.05
    const windLfo = ctx.createOscillator()
    windLfo.frequency.value = 0.05
    const windLfoGain = ctx.createGain()
    windLfoGain.gain.value = 0.03
    windLfo.connect(windLfoGain).connect(windGain.gain)
    windLfo.start()
    noise.connect(bp).connect(windGain).connect(out)
    noise.start()

    this.ambient = {
      stop: () => {
        out.gain.linearRampToValueAtTime(0, ctx.currentTime + 1)
        window.setTimeout(() => {
          oscs.forEach((o) => o.stop())
          lfo.stop()
          noise.stop()
          windLfo.stop()
        }, 1100)
      },
    }
  }

  stopAmbient() {
    this.ambient?.stop()
    this.ambient = null
  }

  /** A plain hit: click, low thump, and a short pitched blip so it reads over the music. */
  playHit(intensity = 1) {
    if (!this.ctx || !this.master) return
    const ctx = this.ctx
    const t = ctx.currentTime
    this.duck()
    this.noiseBurst(t, 2600, 0.05, 0.5 * intensity)
    this.thump(t, 190, 50, 0.26, 0.9 * intensity)
    const o = ctx.createOscillator()
    o.type = 'square'
    o.frequency.setValueAtTime(330, t + 0.01)
    o.frequency.exponentialRampToValueAtTime(140, t + 0.14)
    const g = ctx.createGain()
    g.gain.setValueAtTime(0.14 * intensity, t + 0.01)
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.16)
    o.connect(g).connect(this.master)
    o.start(t + 0.01)
    o.stop(t + 0.18)
  }

  /** Bright ascending two-tone for a super-effective pull. */
  playSuper() {
    if (!this.ctx || !this.master) return
    const ctx = this.ctx
    const t = ctx.currentTime
    this.duck(0.4, 420)
    this.thump(t, 200, 60, 0.18, 0.5)
    this.noiseBurst(t, 1800, 0.1, 0.25)
    ;[660, 990].forEach((f, i) => {
      const o = ctx.createOscillator()
      o.type = 'square'
      o.frequency.value = f
      const g = ctx.createGain()
      const start = t + 0.06 + i * 0.11
      g.gain.setValueAtTime(0.0001, start)
      g.gain.exponentialRampToValueAtTime(0.16, start + 0.015)
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.22)
      o.connect(g).connect(this.master!)
      o.start(start)
      o.stop(start + 0.25)
    })
  }

  /** Sharp crack with a low slam for a critical hit. */
  playCrit() {
    if (!this.ctx || !this.master) return
    const ctx = this.ctx
    const t = ctx.currentTime
    this.duck(0.35, 450)
    this.noiseBurst(t, 3000, 0.06, 0.45)
    this.thump(t + 0.02, 150, 32, 0.32, 0.9)
    const o = ctx.createOscillator()
    o.type = 'sawtooth'
    o.frequency.setValueAtTime(880, t + 0.02)
    o.frequency.exponentialRampToValueAtTime(110, t + 0.3)
    const g = ctx.createGain()
    g.gain.setValueAtTime(0.14, t + 0.02)
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.32)
    o.connect(g).connect(this.master)
    o.start(t + 0.02)
    o.stop(t + 0.35)
  }

  playKo() {
    if (!this.ctx || !this.master) return
    const ctx = this.ctx
    const t = ctx.currentTime
    this.duck(0.3, 700)
    this.thump(t, 120, 28, 0.55, 0.8)
    this.noiseBurst(t, 700, 0.35, 0.35)
    const o = ctx.createOscillator()
    o.type = 'square'
    o.frequency.setValueAtTime(330, t)
    o.frequency.exponentialRampToValueAtTime(70, t + 0.6)
    const g = ctx.createGain()
    g.gain.setValueAtTime(0.12, t)
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.6)
    o.connect(g).connect(this.master)
    o.start(t)
    o.stop(t + 0.65)
  }

  playVictory() {
    if (!this.ctx || !this.master) return
    const ctx = this.ctx
    const t = ctx.currentTime
    ;[523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((f, i) => {
      const o = ctx.createOscillator()
      o.type = 'triangle'
      o.frequency.value = f
      const g = ctx.createGain()
      const start = t + i * 0.13
      g.gain.setValueAtTime(0.0001, start)
      g.gain.exponentialRampToValueAtTime(0.25, start + 0.02)
      g.gain.exponentialRampToValueAtTime(0.001, start + (i === 4 ? 1.6 : 0.5))
      o.connect(g).connect(this.master!)
      o.start(start)
      o.stop(start + 1.7)
    })
  }

  private thump(t: number, from: number, to: number, dur: number, vol: number) {
    const ctx = this.ctx!
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.setValueAtTime(from, t)
    o.frequency.exponentialRampToValueAtTime(to, t + dur)
    const g = ctx.createGain()
    g.gain.setValueAtTime(vol, t)
    g.gain.exponentialRampToValueAtTime(0.001, t + dur)
    o.connect(g).connect(this.master!)
    o.start(t)
    o.stop(t + dur + 0.05)
  }

  private noiseBurst(t: number, freq: number, dur: number, vol: number) {
    const ctx = this.ctx!
    const src = ctx.createBufferSource()
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate)
    const d = buf.getChannelData(0)
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length)
    src.buffer = buf
    const f = ctx.createBiquadFilter()
    f.type = 'bandpass'
    f.frequency.value = freq
    f.Q.value = 0.8
    const g = ctx.createGain()
    g.gain.value = vol
    src.connect(f).connect(g).connect(this.master!)
    src.start(t)
  }
}

export const audio = new BattleAudio()
